// Page theme (D38, control simplified 2026-09-17). What the page RENDERS lives
// on <html data-theme> — always exactly "light" or "dark" — and in
// <meta name="theme-color">. What is STORED is an OVERRIDE, and it exists only
// while it differs from the OS: localStorage "ahcut.theme" holds "light" or
// "dark", or nothing at all. Absent means follow the OS; that is the default,
// and the state a reader returns to by pressing the toggle twice. "system" is
// never written again — a leftover one from the three-option control this
// replaced reads as absent and is deleted on the way past.
//
// The contract runs at three moments and must agree in all of them:
//   1. the inline <head> script in index.html, before the first paint (no flash);
//   2. this module, once React mounts and on every press of the toggle;
//   3. the OS, for as long as no override is stored (matchMedia, live).
// resolveTheme() is that single question — "what should be showing right now?" —
// and nothing on the React side answers it any other way. The inline script is
// the one deliberate transcription of it, because it has to run before this
// bundle exists: edit the two together, and regenerate the CSP hash in
// public/_headers when you touch that script (its comment says how).
//
// Storage is best-effort: a private window or blocked site data makes every read
// and write throw, and the page then follows the OS instead of breaking.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** What the page renders — the value written to <html data-theme>, and the only
 *  value ever stored. There is no "system": that state is the ABSENCE of one. */
export type Theme = "light" | "dark";

const STORAGE_KEY = "ahcut.theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

/** `surface-0` per theme (ui/theme.css). A <meta> cannot read a CSS token, so
 *  these two values are written literally — the sanctioned exception in
 *  ui/DESIGN.md §2, and the same pair the inline script in index.html uses. */
const THEME_COLOR: Record<Theme, string> = {
  light: "#ffffff",
  dark: "#0a0b0d",
};

/** What the OS asks for right now. */
function systemTheme(): Theme {
  return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

/** The stored override, or null when there is none — which means "follow the
 *  OS". A stored "system" is the old control's leftover: it means the same as
 *  absent, so it reads as null and is cleared here rather than kept forever. */
function readOverride(): Theme | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
    if (stored !== null) localStorage.removeItem(STORAGE_KEY);
    return null;
  } catch {
    return null;
  }
}

/** Persist an override, or remove it entirely for "follow the OS" (null). */
function writeOverride(theme: Theme | null): void {
  try {
    if (theme === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Blocked site data: the choice still applies for this page load.
  }
}

/** The theme that should be showing: the override if there is one, the OS
 *  otherwise. The single resolution — see the header note. */
export function resolveTheme(): Theme {
  return readOverride() ?? systemTheme();
}

/** Write the resolved theme to the document: tokens (data-theme) and browser UI. */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[theme]);
}

interface ThemeContextValue {
  /** The theme actually on <html> right now. */
  theme: Theme;
  /** Switch to the opposite of what is showing, storing that choice only while
   *  it differs from the OS. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** One theme state for the whole app: the shell renders the toggle twice
 *  (desktop sidebar and mobile menu) and both must read the same value. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(resolveTheme);

  // Apply on mount (idempotent — the inline script already did it) and follow the
  // OS from then on. The subscription is UNCONDITIONAL and lives for the app's
  // whole life: sync() re-reads the override every time it fires, so a stored
  // override simply wins, and the moment the toggle clears that override the very
  // same subscription resumes repainting the page on an OS flip — no reload, and
  // no torn-down listener to restore. Tearing it down while an override is stored
  // is the bug this shape exists to prevent (ui/DESIGN.md §3).
  useEffect(() => {
    const media = window.matchMedia(DARK_QUERY);
    const sync = () => {
      const next = resolveTheme();
      setTheme(next);
      applyTheme(next);
    };
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  // Flip to the opposite of what is SHOWING. Storing happens only while the
  // choice differs from the OS: when it matches, the override is removed, which
  // hands the page back to the OS, live. Two presses from the OS-matching state
  // therefore return the reader to following the OS — the only way back.
  const toggleTheme = useCallback(() => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    writeOverride(next === systemTheme() ? null : next);
    applyTheme(next);
    setTheme(next);
  }, [theme]);

  const value = useMemo<ThemeContextValue>(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>.");
  return ctx;
}
